import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../utils/useAuth';

export default function Profile() {
  const { user, profile } = useAuth();

  // Basic info
  const [fullName, setFullName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [club, setClub] = useState('');
  const [location, setLocation] = useState('');
  const [union, setUnion] = useState('');
  const [teams, setTeams] = useState([]);
  const [unions, setUnions] = useState([]);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [browserNotifications, setBrowserNotifications] = useState(true);

  // Referee personal info
  const [boksmart, setBoksmart] = useState('');
  const [boksmartExpiry, setBoksmartExpiry] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyNumber, setEmergencyNumber] = useState('');

  // Manager-only evaluation fields
  const [currentLeague, setCurrentLeague] = useState('');
  const [lawExam, setLawExam] = useState('');
  const [fitnessTest, setFitnessTest] = useState('');

  // Email/password updates
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');

  // Load profile into fields
  useEffect(() => {
    if (!profile) return;

    setFullName(profile.fullName || '');
    setContactNumber(profile.contactNumber || '');
    setClub(profile.club || '');
    setLocation(profile.location || '');
    setUnion(profile.union || '');
    setEmailNotifications(profile.emailNotifications !== false); // Default to true
    setBrowserNotifications(profile.browserNotifications !== false); // Default to true

    setBoksmart(profile.boksmart || '');
    setBoksmartExpiry(profile.boksmartExpiry || '');
    setEmergencyName(profile.emergencyName || '');
    setEmergencyNumber(profile.emergencyNumber || '');

    setCurrentLeague(profile.currentLeague || '');
    setLawExam(profile.lawExam || '');
    setFitnessTest(profile.fitnessTest || '');
  }, [profile]);

  // Load teams/schools/clubs
  useEffect(() => {
    async function loadTeams() {
      const snap = await getDocs(collection(db, 'teams'));
      setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }
    loadTeams();
  }, []);

  // Load active/valid unions from licenses
  useEffect(() => {
    async function loadUnions() {
      const snap = await getDocs(collection(db, 'unions'));
      const allUnions = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Filter to only show active and non-expired licenses
      const activeUnions = allUnions.filter(u => {
        if (u.licenseStatus !== 'active') return false;
        if (!u.expirationDate) return true;
        return new Date(u.expirationDate) >= new Date();
      });
      
      setUnions(activeUnions);
    }
    loadUnions();
  }, []);

  // Save basic user fields
  async function saveBasic() {
    await updateDoc(doc(db, "users", user.uid), {
      fullName,
      contactNumber,
      club,
      location,
      union,
      emailNotifications,
      browserNotifications
    });
    alert("Profile updated.");
  }

  // Referee-only save
  async function saveRefereeFields() {
    await updateDoc(doc(db, "users", user.uid), {
      boksmart,
      boksmartExpiry,
      emergencyName,
      emergencyNumber
    });
    alert("Referee information updated.");
  }

  // Manager-only fields save
  async function saveManagerFields() {
    await updateDoc(doc(db, "users", user.uid), {
      currentLeague,
      lawExam,
      fitnessTest
    });
    alert("Manager referee information saved.");
  }

  // Change Email
  async function changeEmail() {
    try {
      const cred = EmailAuthProvider.credential(user.email, emailPw);
      await reauthenticateWithCredential(user, cred);
      await updateEmail(user, newEmail);
      await updateDoc(doc(db, "users", user.uid), { email: newEmail });
      alert("Email updated!");
    } catch (err) {
      alert(err.message);
    }
  }

  // Change Password
  async function changePassword() {
    try {
      const cred = EmailAuthProvider.credential(user.email, oldPw);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, newPw);
      alert("Password updated!");
    } catch (err) {
      alert(err.message);
    }
  }

  if (!profile) return <div className="p-6">Loading...</div>;

  const isReferee = profile.role === "referee";
  const isManager = profile.role === "manager" || profile.role === "admin";

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">

      {/* BASIC PROFILE INFO */}
      <div className="p-6 bg-white rounded shadow">
        <h3 className="text-lg font-semibold mb-4">Profile Information</h3>

        <label className="text-sm">Full name</label>
        <input className="w-full p-2 border rounded mb-3"
          value={fullName} onChange={(e) => setFullName(e.target.value)} />

        <label className="text-sm">Contact number</label>
        <input className="w-full p-2 border rounded mb-3"
          value={contactNumber} onChange={(e) => setContactNumber(e.target.value)} />

        <label className="text-sm">Location</label>
        <input className="w-full p-2 border rounded mb-3"
          value={location} onChange={(e) => setLocation(e.target.value)} />

        {!isReferee && (
          <>
            <label className="text-sm">School/Club</label>
            <select className="w-full p-2 border rounded mb-3"
              value={club} onChange={(e) => setClub(e.target.value)}>
              <option value="">Select School/Club</option>
              {teams.map(team => (
                <option key={team.id} value={team.name}>{team.name} ({team.type})</option>
              ))}
            </select>
          </>
        )}

        <label className="text-sm">Union</label>
        <select className="w-full p-2 border rounded mb-4"
          value={union} onChange={(e) => setUnion(e.target.value)}>
          <option value="">Select Union</option>
          {unions.map(u => (
            <option key={u.id} value={u.name}>{u.name}</option>
          ))}
        </select>

        {/* Notification Preferences */}
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded">
          <h4 className="font-semibold text-gray-900 mb-3">Notification Preferences</h4>
          
          {/* Browser Notifications */}
          <label className="flex items-start cursor-pointer mb-3">
            <input
              type="checkbox"
              checked={browserNotifications}
              onChange={(e) => setBrowserNotifications(e.target.checked)}
              className="w-5 h-5 text-green-600 mr-3 mt-0.5"
            />
            <div>
              <span className="text-sm font-semibold text-gray-900">🔔 Enable Browser Notifications</span>
              <p className="text-xs text-gray-600 mt-1">
                Get instant pop-up notifications on this device for game assignments and updates (recommended)
              </p>
            </div>
          </label>

          {/* Email Notifications */}
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              className="w-5 h-5 text-blue-600 mr-3 mt-0.5"
            />
            <div>
              <span className="text-sm font-semibold text-gray-900">📧 Enable Email Notifications</span>
              <p className="text-xs text-gray-600 mt-1">
                Receive email notifications for game assignments and updates (requires admin setup)
              </p>
            </div>
          </label>
        </div>

        <button onClick={saveBasic} className="bg-green-700 text-white py-2 px-4 rounded">
          Save profile
        </button>
      </div>

      {/* REFEREE PERSONAL INFORMATION */}
      {(isReferee || isManager) && (
        <div className="p-6 bg-white rounded shadow">
          <h3 className="text-lg font-semibold mb-4">Referee Personal Information</h3>

          <label className="text-sm">BokSmart Number</label>
          <input
            className="w-full p-2 border rounded mb-3"
            value={boksmart}
            onChange={(e) => setBoksmart(e.target.value)}
            disabled={!isReferee}
          />

          <label className="text-sm">BokSmart Expiry</label>
          <input
            type="date"
            className="w-full p-2 border rounded mb-3"
            value={boksmartExpiry}
            onChange={(e) => setBoksmartExpiry(e.target.value)}
            disabled={!isReferee}
          />

          <label className="text-sm">Emergency Contact Name</label>
          <input
            className="w-full p-2 border rounded mb-3"
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
            disabled={!isReferee}
          />

          <label className="text-sm">Emergency Contact Number</label>
          <input
            className="w-full p-2 border rounded mb-4"
            value={emergencyNumber}
            onChange={(e) => setEmergencyNumber(e.target.value)}
            disabled={!isReferee}
          />

          {isReferee && (
            <button onClick={saveRefereeFields} className="bg-green-700 text-white py-2 px-4 rounded">
              Save referee information
            </button>
          )}
        </div>
      )}

      {/* MANAGER FIELDS */}
      {isManager && (
        <div className="p-6 bg-white rounded shadow">
          <h3 className="text-lg font-semibold mb-4">Manager fields (for referees)</h3>

          <label className="text-sm">Current league</label>
          <select
            className="w-full p-2 border rounded mb-3"
            value={currentLeague}
            onChange={(e) => setCurrentLeague(e.target.value)}
          >
            <option value="">Select</option>
            <option value="1st League">1st League</option>
            <option value="2nd League">2nd League</option>
            <option value="3rd League">3rd League</option>
            <option value="4th League">4th League</option>
            <option value="Social">Social</option>
            <option value="Beginner">Beginner</option>
          </select>

          <label className="text-sm">Law exam</label>
          <input
            className="w-full p-2 border rounded mb-3"
            value={lawExam}
            onChange={(e) => setLawExam(e.target.value)}
          />

          <label className="text-sm">Fitness test</label>
          <input
            className="w-full p-2 border rounded mb-4"
            value={fitnessTest}
            onChange={(e) => setFitnessTest(e.target.value)}
          />

          <button onClick={saveManagerFields} className="bg-blue-700 text-white py-2 px-4 rounded">
            Save manager fields
          </button>
        </div>
      )}

      {/* EMAIL + PASSWORD */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* CHANGE EMAIL */}
        <div className="p-6 bg-white rounded shadow">
          <h3 className="text-lg font-semibold mb-4">Change Email</h3>

          <label className="text-sm">New Email</label>
          <input className="w-full p-2 border rounded mb-3"
            value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />

          <label className="text-sm">Current Password</label>
          <input type="password" className="w-full p-2 border rounded mb-4"
            value={emailPw} onChange={(e) => setEmailPw(e.target.value)} />

          <button onClick={changeEmail} className="bg-yellow-600 text-white py-2 px-4 rounded">
            Change email
          </button>
        </div>

        {/* CHANGE PASSWORD */}
        <div className="p-6 bg-white rounded shadow">
          <h3 className="text-lg font-semibold mb-4">Change Password</h3>

          <label className="text-sm">Current password</label>
          <input type="password" className="w-full p-2 border rounded mb-3"
            value={oldPw} onChange={(e) => setOldPw(e.target.value)} />

          <label className="text-sm">New password</label>
          <input type="password" className="w-full p-2 border rounded mb-4"
            value={newPw} onChange={(e) => setNewPw(e.target.value)} />

          <button onClick={changePassword} className="bg-red-600 text-white py-2 px-4 rounded">
            Change password
          </button>
        </div>
      </div>

      {/* ACCOUNT INFO */}
      <div className="p-6 bg-white rounded shadow text-sm">
        <h3 className="text-lg font-semibold mb-4">Account Info</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div><strong>UID</strong><br />{user.uid}</div>
            <div className="mt-3"><strong>Approved</strong><br />{profile.approved ? 'true' : 'false'}</div>
            <div className="mt-3"><strong>Created</strong><br />{profile.createdAt?.toDate().toLocaleString()}</div>
          </div>

          <div>
            <div><strong>Role</strong><br />{profile.role}</div>
            <div className="mt-3"><strong>Email</strong><br />{profile.email}</div>
            <div className="mt-3"><strong>Last login</strong><br />{user.metadata.lastSignInTime}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
